import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom-v5-compat';

import { OpFlex } from 'new-components/DLS/OpFlex/OpFlex';
import { OpSection } from 'new-components/DLS/OpSection/OpSection';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { ProductBranding } from 'new-components/ProductBranding/ProductBranding';
import { OpTypography } from 'new-components/DLS/OpTypography/OpTypography';
import { OpText } from 'new-components/DLS/OpText/OpText';
import { OpButton } from 'new-components/DLS/OpButton/OpButton';
import { useIdentityAndUser } from 'utils/customHooks/useIdentityAndUser';
import { useOpMutation } from 'utils/customHooks/useOpMutation';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { getOpParams } from 'utils/getOpParams';
import { useFrameModal } from 'utils/customHooks/useFrameModal';
import { getTermsUrl } from 'utils/getTermsUrl';

import {
  baseRoute as partnerCenterBaseRoute,
  partnerDashboardRoute,
} from '../../constants/routes';

import './TermsOfServicePage.scss';

export default () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orgId } = getOpParams();
  const { identity, user } = useIdentityAndUser();

  const partnerCenterRoute = partnerCenterBaseRoute.replace(
    ':orgId',
    orgId.toString(),
  );

  const {
    data: latestTermsVersion,
    isLoading: isDescribeLatestTermsVersionLoading,
  } = useOpQuery({
    apiEndpointName: 'describeLatestTermsVersion',
    select: (data) => data.json.data,
  });

  const {
    data: hasSignedTermsAgreement,
    isPending: isDescribeOrgPending,
    refetch: refetchDescribeOrg,
    isRefetching: isRefetchingDescribeOrg,
  } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (data) => data.json.data?.termsStatus === 'A',
  });

  const {
    mutateAsync: signOrgTermsAgreement,
    isPending: isSignTermsAgreementLoading,
  } = useOpMutation({
    apiEndpointName: 'signOrgTermsAgreement',
    shouldSuppressErrorMessage: true,
  });

  const { mutateAsync: updateOrg, isPending: isUpdateOrgLoading } =
    useOpMutation({
      apiEndpointName: 'updateOrg',
      shouldSuppressErrorMessage: true,
    });

  const { embeddedUrl, fullScreenUrl } = getTermsUrl(latestTermsVersion);

  const { FrameModal, openModal } = useFrameModal({
    // Using footer link as backup due to occasionally the iframe rendering blank and customers were rightfully mad.
    footer: (
      <OpText className="terms-of-service-page-iframe-footer">
        <a href={fullScreenUrl}>{t('Terms of Service')}</a>
      </OpText>
    ),
    url: embeddedUrl,
    title: 'Terms of Service',
    width: 800,
    height: 480,
  });

  const handleAcceptTerms = async () => {
    if (!identity || !latestTermsVersion || !user) {
      return;
    }

    await signOrgTermsAgreement({
      apiEndpointRequirements: [orgId],
      payload: { termsVersionId: latestTermsVersion.id },
    });

    const {
      email: adminSupportContactEmails,
      mobilePhone: adminSupportContactPhoneNumbers,
    } = identity;

    const updateOrgPayload: Api.Payload['updateOrg'] = {
      adminSupportContactEmails,
      adminSupportContactPhoneNumbers,
    };

    await updateOrg({
      apiEndpointRequirements: [orgId],
      payload: updateOrgPayload,
    });

    /**
     * Await this because we need the latest to be fetched before attempting
     * navigation to avoid a UI "flicker"
     */
    await refetchDescribeOrg();

    navigate(`${partnerCenterRoute}/${partnerDashboardRoute}`);
  };

  // If the org has already signed the terms, redirect to dashboard
  useEffect(() => {
    if (hasSignedTermsAgreement) {
      navigate(`${partnerCenterRoute}/${partnerDashboardRoute}`);
    }
  }, [hasSignedTermsAgreement, navigate, partnerCenterRoute]);

  /**
   * Show button loading treatment during the sequence when:
   *
   * - Creating the terms agreement
   * - Updating the org after signing the terms agreement
   * - Refetching the signed terms agreement (before subsequent navigation)
   */
  const isTermsAgreementSubmitting =
    isSignTermsAgreementLoading ||
    isUpdateOrgLoading ||
    isRefetchingDescribeOrg;

  /**
   * Show page loading treatment when:
   *
   * - Determining whether the org has signed the terms
   * - Loading the latest terms version
   * - User is being redirected to the dashboard
   */
  const isPageLoading = Boolean(
    isDescribeOrgPending ||
      hasSignedTermsAgreement ||
      isDescribeLatestTermsVersionLoading,
  );

  return (
    <OpFlex align="center" justify="center" className="terms-of-service-page">
      <OpSection
        className="terms-of-service-page__section"
        loading={isPageLoading}
      >
        <OpSpace align="center" orientation="vertical" size="large">
          <ProductBranding
            classes={{ text: 'terms-of-service-page__branding-text' }}
            icon={{ width: 24, height: 24 }}
            label={'Partner'}
            testId={'Partner-logo'}
          />

          <OpTypography.Title
            level={1}
            className="terms-of-service-page__title"
          >
            {t('Just one more step')}
          </OpTypography.Title>

          <OpText className="terms-of-service-page__text">
            {t(
              'Before completing registration, you must accept the Avigilon Terms of Service.',
            )}
          </OpText>

          <OpButton
            type="link"
            size="large"
            className="terms-of-service-page__document-link"
            onClick={openModal}
          >
            {t('Review Terms of Service')}
          </OpButton>

          <OpButton
            className="terms-of-service-page__accept-button"
            disabled={isTermsAgreementSubmitting}
            loading={isTermsAgreementSubmitting}
            type="primary"
            onClick={handleAcceptTerms}
          >
            {t('I accept the terms')}
          </OpButton>
        </OpSpace>
      </OpSection>

      {FrameModal}
    </OpFlex>
  );
};
